import React, { useState, Component } from 'react';
import { ArrowLeftIcon, CalendarIcon, StickyNoteIcon, PhoneIcon, MailIcon, MapPinIcon, EditIcon, HashIcon, CopyIcon, CheckIcon, ListTodoIcon, UserPlusIcon, PlusIcon } from 'lucide-react';
import { Customer } from '../components/CustomerCard';
import { Task, TaskCard } from '../components/TaskCard';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';
interface CustomerDetailProps {
  customer: Customer;
  tasks: Task[];
  onBack: () => void;
  onEdit: (customer: Customer) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onAddTask: (customerId: string) => void;
}
export function CustomerDetail({
  customer,
  tasks,
  onBack,
  onEdit,
  onEditTask,
  onDeleteTask,
  onAddTask
}: CustomerDetailProps) {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedSecondaryPhone, setCopiedSecondaryPhone] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | Task['status']>('all');
  const copyToClipboard = async (text: string, type: 'phone' | 'address' | 'secondaryPhone') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'phone') {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      } else if (type === 'address') {
        setCopiedAddress(true);
        setTimeout(() => setCopiedAddress(false), 2000);
      } else if (type === 'secondaryPhone') {
        setCopiedSecondaryPhone(true);
        setTimeout(() => setCopiedSecondaryPhone(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const customerTasks = tasks.filter(t => t.customerId === customer.id);
  const activeTasks = customerTasks.filter(t => t.status === 'in-progress').length;
  const completedTasks = customerTasks.filter(t => t.status === 'done').length;
  const plannedTasks = customerTasks.filter(t => t.status === 'planned').length;
  const pendingTasks = customerTasks.filter(t => t.status === 'pending').length;
  const filteredTasks = selectedStatus === 'all' ? customerTasks : customerTasks.filter(t => t.status === selectedStatus);
  return <div data-id="element-1224">
      <div className="flex items-center gap-4 mb-8" data-id="element-1225">
        <button onClick={onBack} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-all" aria-label="Tilbage" data-id="element-1226">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-1227" />
        </button>
        <div className="flex-1" data-id="element-1228">
          <h1 className="text-4xl font-bold text-stone-900" data-id="element-1229">{customer.name}</h1>
          <div className="flex items-center gap-2 mt-2" data-id="element-1230">
            <HashIcon className="w-4 h-4 text-teal-600" data-id="element-1231" />
            <p className="text-teal-600 font-medium" data-id="element-1232">{customer.customerId}</p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-1233">
          <Button onClick={() => onAddTask(customer.id)} variant="primary" className="flex items-center gap-2" data-id="element-1234">
            <PlusIcon className="w-4 h-4" data-id="element-1235" />
            Tilføj opgave
          </Button>
          <Button onClick={() => onEdit(customer)} variant="secondary" className="flex items-center gap-2" data-id="element-1236">
            <EditIcon className="w-4 h-4" data-id="element-1237" />
            Rediger
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8" data-id="element-1238">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1239">
          <div className="flex items-center gap-3 mb-2" data-id="element-1240">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1241">
              <CalendarIcon className="w-5 h-5 text-blue-700" data-id="element-1242" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1243">
              Opgaver i alt
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1244">
            {customerTasks.length}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.05
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1245">
          <div className="flex items-center gap-3 mb-2" data-id="element-1246">
            <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1247">
              <CalendarIcon className="w-5 h-5 text-amber-700" data-id="element-1248" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1249">
              Aktive opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1250">{activeTasks}</p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1251">
          <div className="flex items-center gap-3 mb-2" data-id="element-1252">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1253">
              <CalendarIcon className="w-5 h-5 text-emerald-700" data-id="element-1254" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1255">
              Afsluttede opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1256">{completedTasks}</p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.15
    }} className="bg-white rounded-xl p-8 shadow-sm border border-stone-100" data-id="element-1257">
        <h2 className="text-2xl font-semibold text-stone-900 mb-6" data-id="element-1258">
          Kundeinformation
        </h2>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-1259">
          {customer.address && <div className="flex items-start gap-4" data-id="element-1260">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1261">
                <MapPinIcon className="w-5 h-5 text-teal-700" data-id="element-1262" />
              </div>
              <div className="flex-1" data-id="element-1263">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1264">
                  Adresse
                </p>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`} target="_blank" rel="noopener noreferrer" className="text-stone-900 hover:text-teal-600 transition-colors underline" data-id="element-1265">
                  {customer.address}
                </a>
              </div>
              <button onClick={() => copyToClipboard(customer.address, 'address')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér adresse" data-id="element-1266">
                {copiedAddress ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1267" /> : <CopyIcon className="w-5 h-5" data-id="element-1268" />}
              </button>
            </div>}

          {customer.phone && <div className="flex items-start gap-4" data-id="element-1269">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1270">
                <PhoneIcon className="w-5 h-5 text-teal-700" data-id="element-1271" />
              </div>
              <div className="flex-1" data-id="element-1272">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1273">
                  Telefon
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1274">{customer.phone}</p>
              </div>
              <button onClick={() => copyToClipboard(customer.phone, 'phone')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1275">
                {copiedPhone ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1276" /> : <CopyIcon className="w-5 h-5" data-id="element-1277" />}
              </button>
            </div>}

          {customer.email && <div className="flex items-start gap-4" data-id="element-1278">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1279">
                <MailIcon className="w-5 h-5 text-teal-700" data-id="element-1280" />
              </div>
              <div className="flex-1" data-id="element-1281">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1282">Email</p>
                <a href={`mailto:${customer.email}`} className="text-stone-900 hover:text-teal-600 transition-colors" data-id="element-1283">
                  {customer.email}
                </a>
              </div>
            </div>}

          {customer.secondaryContactName && <div className="flex items-start gap-4" data-id="element-1284">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1285">
                <UserPlusIcon className="w-5 h-5 text-teal-700" data-id="element-1286" />
              </div>
              <div className="flex-1" data-id="element-1287">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1288">
                  Ekstra kontaktperson
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1289">
                  {customer.secondaryContactName}
                </p>
                {customer.secondaryContactPhone && <div className="flex items-center gap-2 mt-1" data-id="element-1290">
                    <p className="text-stone-600 text-sm" data-id="element-1291">
                      {customer.secondaryContactPhone}
                    </p>
                    <button onClick={() => copyToClipboard(customer.secondaryContactPhone!, 'secondaryPhone')} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1292">
                      {copiedSecondaryPhone ? <CheckIcon className="w-3 h-3 text-teal-600" data-id="element-1293" /> : <CopyIcon className="w-3 h-3" data-id="element-1294" />}
                    </button>
                  </div>}
              </div>
            </div>}
        </div>

        {customer.notes && <div className="mt-6 pt-6 border-t border-stone-200" data-id="element-1295">
            <div className="flex items-start gap-4" data-id="element-1296">
              <div className="p-3 bg-amber-100 rounded-lg" data-id="element-1297">
                <StickyNoteIcon className="w-5 h-5 text-amber-700" data-id="element-1298" />
              </div>
              <div className="flex-1" data-id="element-1299">
                <p className="text-sm font-medium text-stone-500 mb-2" data-id="element-1300">Noter</p>
                <p className="text-stone-700 leading-relaxed" data-id="element-1301">
                  {customer.notes}
                </p>
              </div>
            </div>
          </div>}
      </motion.div>

      {/* Customer Tasks Section */}
      {customerTasks.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.2
    }} className="mt-8" data-id="element-1302">
          <div className="flex items-center justify-between mb-6" data-id="element-1303">
            <div className="flex items-center gap-3" data-id="element-1304">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1305">
                <ListTodoIcon className="w-5 h-5 text-amber-700" data-id="element-1306" />
              </div>
              <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1307">
                Opgaver for {customer.name}
              </h2>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-1308">
            <button onClick={() => setSelectedStatus('all')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'all' ? 'bg-stone-900 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1309">
              Alle ({customerTasks.length})
            </button>
            <button onClick={() => setSelectedStatus('in-progress')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'in-progress' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1310">
              I gang ({activeTasks})
            </button>
            <button onClick={() => setSelectedStatus('done')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'done' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1311">
              Afsluttet ({completedTasks})
            </button>
            <button onClick={() => setSelectedStatus('planned')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'planned' ? 'bg-blue-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1312">
              Planlagt ({plannedTasks})
            </button>
            <button onClick={() => setSelectedStatus('pending')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'pending' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1313">
              Afventende ({pendingTasks})
            </button>
          </div>

          {/* Tasks Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1314">
            {filteredTasks.map((task, index) => <TaskCard key={task.id} task={task} index={index} onClick={() => onEditTask(task)} onDelete={onDeleteTask} data-id="element-1315" />)}
          </div>

          {filteredTasks.length === 0 && <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1316">
              <ListTodoIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" data-id="element-1317" />
              <p className="text-stone-600 font-medium" data-id="element-1318">
                Ingen opgaver i denne kategori
              </p>
              <p className="text-sm text-stone-500 mt-1" data-id="element-1319">
                Prøv at vælge en anden status
              </p>
            </div>}
        </motion.div>}
    </div>;
}